import { Disclosure, DisclosureButton } from "@headlessui/react";
import { Card, Icon, IconButton, Tooltip, Typography } from "@/components/atoms";
import { useAppDispatch } from "@/redux/hooks";
import { setAction } from "@/redux/slices/booking/bookingSlice";
import { BookingDetails } from "@/redux/slices/booking/types";
import { clsx, formatCurrency } from "@/utils";
import { DetailField } from "../../common/DetailField";
import { paymentMethods, paymentStatuses } from "../../fixtures";
import { useIsOperatorOwnJob } from "../../hooks/useIsOperatorOwnJob";

interface PaymentSectionProps {
  booking: BookingDetails;
  showDisclosureButton?: boolean;
  showPaymentMethod?: boolean;
  showPaymentStatus?: boolean;
}

const parseCancellationFee = (itemName: string) => {
  // Parse "Cancellation Fee of 100% applied: cancelled within 0 hours of pickup time"
  const match = itemName.match(/^Cancellation Fee of (\d+)% applied: (.+)$/);
  if (match) {
    const [, percentage, reason] = match;
    return {
      displayName: `Cancellation Fee (${percentage}% applied)`,
      tooltipMessage: reason.charAt(0).toUpperCase() + reason.slice(1),
    };
  }
  return null;
};

export const PaymentSection = ({
  booking,
  showDisclosureButton = true,
  showPaymentMethod = true,
  showPaymentStatus = true,
}: PaymentSectionProps) => {
  const dispatch = useAppDispatch();
  const isOwnJob = useIsOperatorOwnJob();
  const { cost, paymentStatus, metadata, priceSummary } = booking;

  if (metadata.isAcceptedFromNetwork) {
    return null;
  }

  const getPriceSummaryData = () => {
    if (priceSummary) {
      if (priceSummary.isCancelled) {
        return {
          items: priceSummary.breakdown.map((item) => {
            const cancellationData = parseCancellationFee(item.name);
            const shouldShowPlusSign = item.cost >= 0 && !cancellationData?.displayName;
            return {
              label: cancellationData?.displayName || item.name,
              value:
                (shouldShowPlusSign ? "+" : "") + formatCurrency(item.cost, 2, priceSummary.currency.name, { skipDecimalConversion: true }),
              tooltipMessage: cancellationData?.tooltipMessage,
            };
          }),
          total: formatCurrency(priceSummary.total, 2, priceSummary.currency.name, { skipDecimalConversion: true }),
          isCancelled: true,
          hasBreakdown: priceSummary.breakdown.length > 0,
        };
      } else if (priceSummary.breakdown.length === 0) {
        return {
          items: [],
          total: formatCurrency(priceSummary.total, 2, priceSummary.currency.name, { skipDecimalConversion: true }),
          isCancelled: false,
          hasBreakdown: false,
        };
      } else {
        return {
          items: priceSummary.breakdown.map((item) => {
            const cancellationData = parseCancellationFee(item.name);
            const shouldShowPlusSign = item.cost >= 0 && !cancellationData?.displayName;
            return {
              label: cancellationData?.displayName || item.name,
              value:
                (shouldShowPlusSign ? "+" : "") + formatCurrency(item.cost, 2, priceSummary.currency.name, { skipDecimalConversion: true }),
              tooltipMessage: cancellationData?.tooltipMessage,
            };
          }),
          total: formatCurrency(priceSummary.total, 2, priceSummary.currency.name, { skipDecimalConversion: true }),
          isCancelled: false,
          hasBreakdown: true,
        };
      }
    }

    return {
      items: [{ label: "Base Trip Cost", value: formatCurrency(cost.total, 2, cost.currency), tooltipMessage: undefined }],
      total: formatCurrency(cost.total, 2, cost.currency),
      isCancelled: false,
      hasBreakdown: true,
    };
  };

  const priceData = getPriceSummaryData();

  const handlePaymentStatus = () => {
    dispatch(setAction("payment-logs"));
  };

  const rows: Array<React.ReactNode> = [];

  rows.push(
    <DetailField
      key="price-summary"
      leading={<Icon name="DollarCircle" size="lg" className="text-neutral-dark-gray" />}
      label="Price Summary"
      indent={false}
      value={
        priceData.hasBreakdown ? (
          showDisclosureButton ? (
            <Disclosure as="div">
              {({ open }) => (
                <>
                  <div
                    className={clsx(
                      "grid pl-8 transition-[grid-template-rows] duration-200 ease-in-out",
                      open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    )}
                  >
                    <div className="space-y-2 overflow-hidden pt-2">
                      {priceData.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between gap-1">
                          <Typography variant="paragraph">
                            {item.label}
                            {item.tooltipMessage && (
                              <Tooltip content={item.tooltipMessage} placement="top">
                                <Icon
                                  name="InfoCircle"
                                  className="inline-block translate-x-1 rotate-180 text-neutral-dark-gray"
                                  size="sm"
                                />
                              </Tooltip>
                            )}
                          </Typography>
                          <div className="w-20 text-right">
                            <Typography variant="paragraph">{item.value}</Typography>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={clsx("flex items-center justify-between pl-8", open ? "pt-2" : "pt-0")}>
                    <Typography variant="action">Total Cost</Typography>
                    <Typography variant="action">{priceData.total}</Typography>
                  </div>

                  <div className="flex items-center justify-center pt-3">
                    <DisclosureButton
                      aria-label={open ? "Collapse price details" : "Expand price details"}
                      className="inline-flex w-full items-center justify-center rounded-md p-0 hover:bg-neutral-gray"
                    >
                      <Icon name={open ? "ArrowUp2" : "ArrowDown2"} className="text-neutral-dark-gray" />
                    </DisclosureButton>
                  </div>
                </>
              )}
            </Disclosure>
          ) : (
            <>
              <div className="space-y-2 pl-8 pt-2">
                {priceData.items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between gap-1">
                    <Typography variant="paragraph">
                      {item.label}
                      {item.tooltipMessage && (
                        <Tooltip content={item.tooltipMessage} placement="top">
                          <Icon name="InfoCircle" className="inline-block translate-x-1 rotate-180 text-neutral-dark-gray" size="sm" />
                        </Tooltip>
                      )}
                    </Typography>
                    <div className="w-20 text-right">
                      <Typography variant="paragraph">{item.value}</Typography>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between pl-8 pt-2">
                <Typography variant="action">Total Cost</Typography>
                <Typography variant="action">{priceData.total}</Typography>
              </div>
            </>
          )
        ) : (
          <div className="pl-8">
            <Typography variant="action">{priceData.total}</Typography>
          </div>
        )
      }
    />
  );

  if (showPaymentMethod) {
    rows.push(
      <DetailField
        key="payment-method"
        leading={<Icon name="Card" size="lg" className="text-neutral-dark-gray" />}
        label="Payment Method"
        value={paymentMethods[cost.paymentMethod] ?? ""}
      />
    );
  }

  if (showPaymentStatus) {
    rows.push(
      <DetailField
        key="payment-status"
        leading={
          <div className="flex h-6 w-6 items-center justify-center">
            <div className={`h-[10px] w-[10px] rounded-full bg-neutral-dark-gray`} />
          </div>
        }
        label="Payment Status (Logs)"
        value={paymentStatus ? paymentStatuses[paymentStatus] : "-"}
        right={
          !metadata.isOffloaded || isOwnJob ? (
            <Tooltip content="View Payment Logs" placement="top">
              <IconButton
                iconName="ArrowRight"
                iconSize="md"
                size="xs"
                variant="primary"
                className="h-8 w-8"
                onClick={handlePaymentStatus}
              />
            </Tooltip>
          ) : null
        }
      />
    );
  }

  return <Card className="space-y-3 p-2.5">{rows}</Card>;
};
